/**
 * <media-gallery> progressively enhances snippets/product-gallery.liquid:
 * - Desktop: vertical thumb rail, click swaps the active main slide
 *   (others are display:none via CSS .is-active toggling).
 * - Mobile: the main slide row becomes a native scroll-snap swipe strip
 *   (CSS media query forces every slide visible + flex: 0 0 100%); an
 *   IntersectionObserver watches which slide is in view and syncs the
 *   thumb/dot active state and, on thumb/dot click, scrolls the strip.
 * - Click a static image slide to open the <dialog> lightbox at full res,
 *   with prev/next controls and Esc/backdrop close (native <dialog>
 *   handles Esc; backdrop click is handled manually below since dialog's
 *   own box IS the backdrop when shown modally).
 *
 * Public API used by assets/variant-picker.js: goToMedia(mediaId) - swaps
 * the active slide/thumb for a variant's featured media, no-op if that
 * media id isn't part of the gallery.
 *
 * Without JS the thumb rail and dots are inert buttons and the main slide
 * row is still swipeable (scroll-snap is CSS-only), so this is enhancement
 * only - nothing here is required for the gallery to be usable.
 */
if (!customElements.get('media-gallery')) {
  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.main = this.querySelector('[data-gallery-main]');
      this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
      /* The frame carries the direction of travel for the CSS - see setActive. */
      this.frame = this.querySelector('.product-gallery__main');
      this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
      this.dots = Array.from(this.querySelectorAll('[data-gallery-dot]'));
      this.lightbox = this.querySelector('[data-gallery-lightbox]');
      this.lightboxStage = this.querySelector('[data-gallery-lightbox-stage]');
      this.lightboxImages = Array.from(this.querySelectorAll('[data-gallery-lightbox-image]'));
      if (!this.main || this.slides.length === 0) return;

      this.onThumbClick = this.onThumbClick.bind(this);
      this.onDotClick = this.onDotClick.bind(this);
      this.onMainClick = this.onMainClick.bind(this);
      this.onLightboxClick = this.onLightboxClick.bind(this);
      this.onLightboxKeydown = this.onLightboxKeydown.bind(this);
      this.onLightboxClosed = this.onLightboxClosed.bind(this);
      this.onLightboxCancel = this.onLightboxCancel.bind(this);
      this.lightboxTrigger = null;

      this.thumbs.forEach((thumb) => thumb.addEventListener('click', this.onThumbClick));

      this.prevBtn = this.querySelector('[data-gallery-prev]');
      this.nextBtn = this.querySelector('[data-gallery-next]');
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.step(-1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.step(1));

      this.railPrev = this.querySelector('[data-gallery-rail-prev]');
      this.railNext = this.querySelector('[data-gallery-rail-next]');
      this.thumbRail = this.querySelector('[data-gallery-thumbs]');
      if (this.railPrev) this.railPrev.addEventListener('click', () => this.scrollRail(-1));
      if (this.railNext) this.railNext.addEventListener('click', () => this.scrollRail(1));
      if (this.thumbRail) {
        /* A vertical wheel over a horizontal scroller does nothing by default,
           so the rail looked stuck to anyone who tried the obvious thing. This
           forwards vertical wheel movement to horizontal scroll.

           preventDefault is deliberate and needs passive:false: without it the
           page scrolls at the same time, and the rail slides sideways while
           the whole gallery leaves the screen. Only prevented when the rail
           can actually move, so at either end the wheel goes back to scrolling
           the page rather than trapping the visitor in a strip that has
           nothing left to show. */
        this.thumbRail.addEventListener(
          'wheel',
          (event) => {
            if (event.deltaY === 0) return;
            const max = this.thumbRail.scrollWidth - this.thumbRail.clientWidth;
            if (max <= 0) return;
            const next = this.thumbRail.scrollLeft + event.deltaY;
            if (next < 0 || next > max) return;
            event.preventDefault();
            this.thumbRail.scrollLeft = next;
          },
          { passive: false }
        );

        this.thumbRail.addEventListener('scroll', () => this.syncRailButtons(), { passive: true });
        window.addEventListener('resize', () => this.syncRailButtons());
        this.syncRailButtons();
      }
      /* On load the first image is active, so prev must already be disabled -
         setActive has not run yet and will not until something is clicked. */
      this.syncNavButtons();

      /* Arrow keys move through the images when anything in the gallery has
         focus. Scoped to the gallery rather than the document, or the keys
         would hijack arrow-key page scrolling for the whole page. */
      this.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        if (this.lightbox && this.lightbox.open) return;
        event.preventDefault();
        this.step(event.key === 'ArrowRight' ? 1 : -1);
      });
      this.dots.forEach((dot) => dot.addEventListener('click', this.onDotClick));
      this.main.addEventListener('click', this.onMainClick);
      /* The full-size images are lazy inside the closed lightbox, so they
         only started downloading on the click and the lightbox opened on a
         blank stage. Pointing at (or touching) an image starts its full-size
         copy early. */
      this.onZoomIntent = (event) => {
        const zoomButton = event.target.closest && event.target.closest('[data-gallery-zoom]');
        if (zoomButton) this.warmLightboxImage(zoomButton.dataset.mediaId);
      };
      this.main.addEventListener('pointerover', this.onZoomIntent, { passive: true });
      this.main.addEventListener('pointerdown', this.onZoomIntent, { passive: true });
      this.main.addEventListener('focusin', this.onZoomIntent);

      if (this.lightbox) {
        this.querySelectorAll('[data-gallery-lightbox-close]').forEach((btn) =>
          btn.addEventListener('click', () => this.closeLightbox())
        );
        this.querySelectorAll('[data-gallery-lightbox-prev]').forEach((btn) =>
          btn.addEventListener('click', () => this.stepLightbox(-1))
        );
        this.querySelectorAll('[data-gallery-lightbox-next]').forEach((btn) =>
          btn.addEventListener('click', () => this.stepLightbox(1))
        );
        this.lightbox.addEventListener('click', this.onLightboxClick);
        this.lightbox.addEventListener('keydown', this.onLightboxKeydown);
        this.lightbox.addEventListener('close', this.onLightboxClosed);
        this.lightbox.addEventListener('cancel', this.onLightboxCancel);
      }

      /* This observer exists for the MOBILE swipe strip only, where the slides
         are laid side by side and scrolling genuinely changes which one the
         visitor is looking at.

         It must not run on desktop. There the slides are stacked and the
         outgoing one is deliberately held at full visibility while the
         incoming slide covers it - so the observer would fire immediately and
         set the OUTGOING slide active again, snapping the gallery back to the
         image the visitor just left. That is what made a thumbnail need two
         clicks: the first one worked and was instantly undone.

         The query matches the breakpoint in component-gallery.css where the
         frame becomes a horizontal scroller. Checked at call time rather than
         at setup, so a resize does not leave it wired to the wrong layout. */
      /* Defined unconditionally: setActive reads it too, and it must exist
         even when there is only one slide or no IntersectionObserver. */
      this.swipeQuery = window.matchMedia('(max-width: 749px)');

      if (this.slides.length > 1 && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            if (!this.swipeQuery.matches) return;
            entries.forEach((entry) => {
              if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
                this.setActive(entry.target.dataset.mediaId, { scroll: false });
              }
            });
          },
          { root: this.main, threshold: [0.6] }
        );
        this.slides.forEach((slide) => this.observer.observe(slide));
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.lightbox) {
        this.lightbox.removeEventListener('close', this.onLightboxClosed);
        this.lightbox.removeEventListener('cancel', this.onLightboxCancel);
        if (window.ThemeOverlay) {
          window.ThemeOverlay.cancelClose(this.lightbox);
          /* A gallery removed while its lightbox is open - which the theme
             editor does on every section re-render - would otherwise take the
             scroll lock to the grave with it, leaving the page frozen with no
             overlay on screen to explain why. */
          window.ThemeOverlay.unlockScroll(this);
        }
      }
    }

    onThumbClick(event) {
      const thumb = event.currentTarget;
      this.setActive(thumb.dataset.mediaId, { scroll: true });
    }

    onDotClick(event) {
      const dot = event.currentTarget;
      this.setActive(dot.dataset.mediaId, { scroll: true });
    }

    onMainClick(event) {
      const zoomButton = event.target.closest('[data-gallery-zoom]');
      if (!zoomButton) return;
      this.openLightbox(zoomButton.dataset.mediaId, zoomButton);
    }

    // Public: used by variant-picker.js when the selected variant has its
    // own featured media.
    goToMedia(mediaId) {
      if (!mediaId) return;
      const exists = this.slides.some((slide) => slide.dataset.mediaId === mediaId);
      if (exists) this.setActive(mediaId, { scroll: true });
    }

    /* Steps one image in either direction. Deliberately CLAMPED rather than
       wrapping: it used to clamp, on the argument that the thumbnail rail shows
       the visitor where the ends are, so looping would contradict it. The owner
       asked for the opposite - past the last image should come back round to
       the first, as a circular loop - which is also what the hero carousel
       already does (hero-carousel.js steps with the same modulo). A gallery
       that dead-ends is the odd one out in this theme, and the thumbnails stay
       honest either way because the active one is always scrolled into view. */
    step(delta) {
      const total = this.slides.length;
      if (total < 2) return;
      const current = this.slides.findIndex((slide) => slide.classList.contains('is-active'));
      if (current === -1) return;
      /* Modulo twice, because a negative delta on image 0 yields a negative
         index and JavaScript's % keeps the sign. */
      const next = (((current + delta) % total) + total) % total;
      if (next === current) return;
      this.setActive(this.slides[next].dataset.mediaId, { scroll: true });
    }

    /* Scrolls the rail by one visible width, so nothing is stepped over. Using
       the rail's own clientWidth rather than a thumbnail count means it stays
       correct at every breakpoint without knowing how many fit. */
    scrollRail(delta) {
      if (!this.thumbRail) return;
      this.thumbRail.scrollBy({ left: delta * this.thumbRail.clientWidth, behavior: 'smooth' });
    }

    /* Hidden, not disabled, when there is nothing to scroll: a product with
       four images should show no controls at all rather than two dead ones.
       Disabled only at the ends, where the control is still meaningful.

       The 2px tolerance matters - scrollLeft is fractional on zoomed or
       high-DPI displays, so an exact comparison leaves the end button
       permanently enabled. */
    syncRailButtons() {
      if (!this.thumbRail || !this.railPrev || !this.railNext) return;
      const scrollable = this.thumbRail.scrollWidth - this.thumbRail.clientWidth > 2;
      this.railPrev.hidden = !scrollable;
      this.railNext.hidden = !scrollable;
      if (!scrollable) return;
      this.railPrev.disabled = this.thumbRail.scrollLeft <= 2;
      this.railNext.disabled =
        this.thumbRail.scrollLeft >= this.thumbRail.scrollWidth - this.thumbRail.clientWidth - 2;
    }

    /* Keeps the active thumbnail in view. This scrolls the RAIL, which is what
       it is for - not the media frame, which is overflow:hidden with slides
       translated a full width aside, and scrolling that empties the gallery.
       The two were the same call once; they are not the same thing. */
    revealActiveThumb() {
      if (!this.thumbRail) return;
      const active = this.thumbs.find((thumb) => thumb.classList.contains('is-active'));
      if (!active) return;
      const railRect = this.thumbRail.getBoundingClientRect();
      const thumbRect = active.getBoundingClientRect();
      if (thumbRect.left >= railRect.left && thumbRect.right <= railRect.right) return;
      this.thumbRail.scrollBy({
        left: thumbRect.left - railRect.left - (railRect.width - thumbRect.width) / 2,
        behavior: 'smooth'
      });
    }

    setActive(mediaId, options) {
      const scroll = Boolean(options && options.scroll);

      /* Direction of travel, so the incoming image can slide UP when moving
         forward through the gallery and DOWN when going back. Always-up looks
         wrong the moment the visitor clicks a thumbnail above the current one:
         the picture arrives from the direction they just came from.

         Written on the frame rather than on the slide because the CSS needs it
         while BOTH the outgoing and incoming slides are still present, and the
         incoming one has no way to know where the previous image sat. */
      const nextIndex = this.slides.findIndex((slide) => slide.dataset.mediaId === mediaId);
      const currentIndex = this.slides.findIndex((slide) => slide.classList.contains('is-active'));
      if (this.frame && nextIndex !== -1 && currentIndex !== -1 && nextIndex !== currentIndex) {
        this.frame.dataset.direction = nextIndex > currentIndex ? 'forward' : 'back';

        /* Force the browser to apply that attribute BEFORE .is-active is
           toggled below. Without this the two land in the same frame, so the
           incoming slide never gets repositioned to the far side - it simply
           transitions from wherever it was already parked, and going backwards
           looks identical to going forwards.

           Reading offsetHeight is what forces the recalculation. Parked slides
           carry no transition, so the reposition is instantaneous and happens
           outside the frame where nothing can see it. */
        void this.frame.offsetHeight;
      }

      /* Mark the slide being replaced so CSS can hold it still while the new
         one covers it. Without this it would spring to its parked position the
         instant it lost .is-active, and BOTH images would be moving - which
         reads as a shuffle rather than a replacement.

         It keeps the mark until the next swap rather than clearing on a timer:
         by then it is completely covered by the active slide, so where it
         jumps to is invisible. No timer, nothing to get out of step. */
      this.slides.forEach((slide) => {
        slide.classList.toggle('was-active', slide.classList.contains('is-active') && slide.dataset.mediaId !== mediaId);
      });

      this.slides.forEach((slide) => slide.classList.toggle('is-active', slide.dataset.mediaId === mediaId));
      this.thumbs.forEach((thumb) => {
        const isActive = thumb.dataset.mediaId === mediaId;
        thumb.classList.toggle('is-active', isActive);
        thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
      this.dots.forEach((dot) => dot.classList.toggle('is-active', dot.dataset.mediaId === mediaId));

      /* Scrolling the frame is the MOBILE swipe mechanism and must not run on
         desktop, for the same reason the observer above must not: there the
         slides are absolutely positioned and translated, which gives the frame
         a scrollWidth of twice its own width even though it is overflow:hidden.
         scrollIntoView happily scrolls it, and the active slide ends up a full
         frame-width off to the left - so the gallery looks EMPTY.

         overflow:hidden prevents the user scrolling a box. It does not prevent
         script from scrolling it, which is the trap here. */
      this.revealActiveThumb();
      this.syncRailButtons();
      this.syncNavButtons();

      if (scroll && this.swipeQuery && this.swipeQuery.matches) {
        const target = this.slides.find((slide) => slide.dataset.mediaId === mediaId);
        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      }
    }

    /* Never disabled now that step() wraps: at either end the button still goes
       somewhere, so greying it out would be lying about what it does. They are
       only switched off when there is nothing to step through at all. */
    syncNavButtons() {
      if (!this.prevBtn || !this.nextBtn) return;
      const dead = this.slides.length < 2;
      this.prevBtn.disabled = dead;
      this.nextBtn.disabled = dead;
    }

    openLightbox(mediaId, triggerEl) {
      if (!this.lightbox || typeof this.lightbox.showModal !== 'function') return;
      this.setLightboxImage(mediaId);
      this.lightboxTrigger = triggerEl || null;
      // Shared with the cart drawer, quick view and the concierge modal -
      // see assets/overlay-motion.js.
      if (window.ThemeOverlay) {
        window.ThemeOverlay.openDialog(this.lightbox);
        /* Every other overlay in the theme locks scroll itself - the cart
           drawer, quick view, the concierge modal - and the lightbox was the
           only one that did not, so the page scrolled away behind a
           full-screen image. showModal() makes the page inert but does NOT
           stop it scrolling. */
        window.ThemeOverlay.lockScroll(this);
      } else {
        this.lightbox.showModal();
        this.lightbox.classList.add('is-visible');
      }
    }

    closeLightbox() {
      if (!this.lightbox || !this.lightbox.open) return;
      if (window.ThemeOverlay) {
        window.ThemeOverlay.closeDialog(this.lightbox);
        window.ThemeOverlay.unlockScroll(this);
      } else {
        this.lightbox.close();
      }
    }

    /* Esc closes a <dialog> natively: the browser fires `cancel`, then closes
       it immediately, then fires `close`. That bypasses closeLightbox()
       entirely - which was the ONLY place unlockScroll ran - so html.overlay-
       open (overflow: hidden, base.css) stayed on and THE WHOLE PAGE WAS
       UNSCROLLABLE after exiting the lightbox. It also skipped the exit
       animation, which is the "bugs out while exiting" half of the report.

       Intercepting `cancel` and routing Esc back through closeLightbox() fixes
       both at once: same teardown, same animation, whichever way it is closed.
       The cart drawer already does exactly this - assets/cart.js:254 - and the
       gallery was the one overlay that did not. */
    onLightboxCancel(event) {
      event.preventDefault();
      this.closeLightbox();
    }

    // The lightbox previously restored focus nowhere - closing it (Esc,
    // backdrop, or the close button) dropped a keyboard user back at the
    // top of the document, unlike every other overlay in the theme.
    onLightboxClosed() {
      this.lightbox.classList.remove('is-visible');
      if (window.ThemeOverlay) {
        window.ThemeOverlay.cancelClose(this.lightbox);
        /* Belt and braces. `cancel` is intercepted above so Esc now unlocks
           through closeLightbox(), but a <dialog> can also be closed by script
           or by a form[method=dialog] submit, neither of which fires `cancel`.
           unlockScroll is reference-counted by owner in overlay-motion.js, so
           calling it twice for one open is harmless - whereas missing it once
           freezes the page. */
        window.ThemeOverlay.unlockScroll(this);
        window.ThemeOverlay.restoreFocus(this.lightboxTrigger);
      }
      this.lightboxTrigger = null;
    }

    warmLightboxImage(mediaId) {
      const img = this.lightboxImages.find((image) => image.dataset.mediaId === mediaId);
      if (img && img.loading === 'lazy') img.loading = 'eager';
    }

    /* The image shown plus its neighbours, so stepping through is not a
       blank stage per click either. */
    warmAroundLightbox() {
      const count = this.lightboxImages.length;
      if (!count) return;
      [0, 1, -1].forEach((offset) => {
        const img = this.lightboxImages[(this.lightboxIndex + offset + count) % count];
        if (img && img.loading === 'lazy') img.loading = 'eager';
      });
    }

    setLightboxImage(mediaId) {
      if (!this.lightboxImages.length) return;
      let index = this.lightboxImages.findIndex((img) => img.dataset.mediaId === mediaId);
      if (index === -1) index = 0;
      this.applyLightboxIndex(index, index > this.lightboxIndex ? 'forward' : 'back');
    }

    stepLightbox(delta) {
      if (!this.lightboxImages.length) return;
      const next = (this.lightboxIndex + delta + this.lightboxImages.length) % this.lightboxImages.length;
      this.applyLightboxIndex(next, delta > 0 ? 'forward' : 'back');
    }

    /* Shares the main gallery's approach: mark the outgoing image so it can
       travel out while the incoming one travels in, and force the direction
       to apply BEFORE the classes change - otherwise the incoming image
       transitions from wherever it was already parked and going back looks
       identical to going forward. */
    applyLightboxIndex(index, direction) {
      const stage = this.lightbox.querySelector('[data-gallery-lightbox-stage]');
      if (stage) {
        stage.dataset.direction = direction;
        void stage.offsetHeight;
      }

      this.lightboxImages.forEach((img) => {
        img.classList.toggle('was-active', img.classList.contains('is-active'));
      });
      this.lightboxImages.forEach((img, i) => img.classList.toggle('is-active', i === index));
      this.lightboxIndex = index;
      this.warmAroundLightbox();
    }

    onLightboxClick(event) {
      /* A click during the close animation. The modal is still up, so the
         page underneath is inert and the click lands here instead of on the
         image the visitor clicked again - which is why reopening the zoom
         straight after closing it did nothing. Hit-test the gallery images
         by position (elementsFromPoint skips inert content) and reopen. */
      if (window.ThemeOverlay && window.ThemeOverlay.isClosing && window.ThemeOverlay.isClosing(this.lightbox)) {
        const zoomButton = Array.from(this.main.querySelectorAll('[data-gallery-zoom]')).find((el) => {
          const rect = el.getBoundingClientRect();
          return rect.width > 0 && event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
        });
        if (zoomButton) this.openLightbox(zoomButton.dataset.mediaId, zoomButton);
        return;
      }
      // A click landing directly on the <dialog> element (not one of its
      // rendered children) is a backdrop click.
      if (event.target === this.lightbox) this.closeLightbox();
    }

    onLightboxKeydown(event) {
      if (event.key === 'ArrowLeft') this.stepLightbox(-1);
      if (event.key === 'ArrowRight') this.stepLightbox(1);
    }
  }

  customElements.define('media-gallery', MediaGallery);
}
